// Shared billing formatters. Kept in one place so the Billing page and the billing saga can't drift
// (they previously each defined their own fmtDate with different empty fallbacks).

/**
 * Format a date as en-AU "02 Jun 2026". Returns `fallback` when the date is missing.
 * Callers that interpolate the result into a sentence pass `null` so the empty case is falsy;
 * display callers take the default em-dash.
 */
export const formatDateAU = (d, fallback = '—') =>
	d ? new Date(d).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' }) : fallback;

/**
 * Format a date AND time as en-AU "19 Aug 2026, 9:01 pm AEST".
 *
 * Rendered in `timeZone` — the site's own zone, not the viewer's — because these timestamps are
 * operational: a pass configured to end at 9pm ends at 9pm WHERE THE GATE IS. Showing a Sydney
 * pass in Perth local time would misreport it by two hours, so the zone is labelled whenever one
 * is known. Falls back to the viewer's local zone (unlabelled) when the record carries none.
 *
 * ⚠️ Pairs with formatDateAU above and lives beside it deliberately — a date-only render of a
 * timestamp that carries a meaningful time reads as precise while withholding the only part that
 * matters ("valid to 22 Aug" says nothing about a 10am checkout).
 */
export const formatDateTimeAU = (d, timeZone, fallback = '—') => {
	if (!d) return fallback;
	const date = new Date(d);
	if (Number.isNaN(date.getTime())) return fallback;
	const opts = {
		day: '2-digit',
		month: 'short',
		year: 'numeric',
		hour: 'numeric',
		minute: '2-digit',
		hour12: true
	};
	if (timeZone) {
		opts.timeZone = timeZone;
		opts.timeZoneName = 'short';
	}
	try {
		return date.toLocaleString('en-AU', opts);
	} catch (_) {
		// An unrecognised IANA zone would otherwise throw and blank the whole grid cell.
		delete opts.timeZone;
		delete opts.timeZoneName;
		return date.toLocaleString('en-AU', opts);
	}
};

/** Format integer cents as en-AU currency ("$1,234.00"). Returns em-dash when null/undefined. */
export const formatAUD = (cents) =>
	cents == null ? '—' : (cents / 100).toLocaleString('en-AU', { style: 'currency', currency: 'AUD' });
